import { Fragment, memo, ReactNode, useEffect, useMemo, useState, useRef } from 'react'
import * as S from './styles'
import { NotFound, Checkbox, CircularProgress, SortLabel } from 'components'
import { Collapse } from '@mui/material'
import { theme } from 'styles/theme/hav-theme'

export type TextAlignOptions = 'left' | 'center' | 'right'

type ColumnAlign = {
  colIndex: number
  textAlign: TextAlignOptions
}

type BodyData = {
  id: string
  checked?: boolean
  disableCheckbox?: boolean
  disabled?: boolean
  expanded?: boolean
  content: ReactNode[]
  subRows?: {
    id: string
    content: ReactNode[]
  }[]
  linkNavigate?: string
  colunsNotNavigate?: number[]
  onClick?: () => void
  onDoubleClick?: () => void
}

export type HeadItemType = {
  label: string | JSX.Element
  columnName: string
  actionsLabel?: boolean
  sortable?: boolean
}

export type SortFilterType = {
  column?: string
  isCrescent?: boolean
}

export type TableProps = {
  headData: string[] | ReactNode[]
  bodyData: BodyData[]
  toggleExpand?: (id: string) => void
  hasCheckbox?: boolean
  disableGlobalCheckbox?: boolean
  checkboxSize?: 'default' | 'small'
  setChecked?: (checked: boolean, id?: string) => void
  summaryRow?: ReactNode[]
  notFoundTitle?: string
  notFoundDescription?: string | JSX.Element
  distributeWidth?: boolean
  notScrollable?: boolean
  maxHeight?: number | string
  rowHeight?: number
  fixedHead?: boolean
  fixedFirstCol?: boolean
  fixedLeftCols?: number[]
  fixedLeftColsWidth?: number
  customFixedLeftColsWidth?: number[]
  dense?: boolean
  valign?: 'middle' | 'bottom' | 'baseline' | 'top'
  actionWidth?: number
  actionColumns?: number[]
  iconColumns?: number[]
  inputAlign?: TextAlignOptions
  customAlign?: ColumnAlign[]
  tableRef?: React.MutableRefObject<null>
  isPrinting?: boolean
  isLoading?: boolean
  hasAsyncExpand?: boolean
  iconColor?: string
  sortValue?: SortFilterType
  sortHandle?: (sortFilter: SortFilterType) => void
  onScroll?: () => void
}

const hasExpandableRows = (bodyData: BodyData[]) =>
  useMemo(() => bodyData.some((item) => item.subRows?.length), [bodyData])

export const Table = ({
  headData,
  bodyData,
  hasCheckbox = false,
  disableGlobalCheckbox = false,
  checkboxSize = 'default',
  toggleExpand = () => {
    return
  },
  setChecked = () => {
    return
  },
  summaryRow,
  notFoundDescription = (
    <>
      Não encontramos o que você procurou.
      <br />
      Verifique os critérios utilizados e tente novamente.
    </>
  ),
  notFoundTitle = 'Nenhum item encontrado',
  distributeWidth = true,
  notScrollable = false,
  maxHeight = 'auto',
  rowHeight = 67,
  fixedHead = false,
  fixedFirstCol = false,
  fixedLeftCols = [],
  fixedLeftColsWidth = 100,
  customFixedLeftColsWidth = [],
  dense = false,
  valign = undefined,
  actionWidth = 100,
  actionColumns = [],
  iconColumns = [],
  inputAlign,
  customAlign = [],
  tableRef,
  isPrinting = false,
  isLoading = false,
  hasAsyncExpand = false,
  iconColor = theme.colors.primary.main,
  sortValue,
  sortHandle,
  onScroll
}: TableProps) => {
  const colSpan = hasCheckbox ? headData.length + 1 : headData.length
  const [linkMouseDown, setLinkMouseDown] = useState(false)
  const [linkDragActive, setLinkDragActive] = useState(false)
  const [sortFilter, setSortFilter] = useState<SortFilterType>({})

  const [expandedId, setExpandedId] = useState('')

  const handleLinkDown = () => setLinkMouseDown(true)
  const handleLinkUp = () => setLinkMouseDown(false)
  const handleLinkMove = () => {
    if (linkMouseDown) setLinkDragActive(true)
    else setLinkDragActive(false)
  }
  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (linkDragActive) e.preventDefault()
  }

  const verifyAllChecked = () => {
    return bodyData.every((item) => item.checked || item.disableCheckbox)
  }
  const verifyAllDisabled = () => {
    return bodyData.every((item) => item.disableCheckbox)
  }
  const verifySomeChecked = () => {
    if (!verifyAllChecked()) return bodyData.some((item) => item.checked)
    else return false
  }

  const handleGlobalCheckbox = () => {
    if (verifyAllChecked()) {
      setChecked(false)
    } else {
      setChecked(true)
    }
  }

  const isIconCol = (index: number) => {
    return iconColumns.includes(index)
  }

  const isFixedCol = (index: number) => {
    return fixedFirstCol && index === 0
  }

  const isFixedLeft = (index: number) => {
    return fixedLeftCols.includes(index)
  }

  const isFixedAction = (index: number) => {
    return actionColumns.includes(index)
  }

  const getFixedLeftOffset = (index: number) => {
    if (customFixedLeftColsWidth.length) {
      const colsOffset = customFixedLeftColsWidth
        .slice(0, index)
        .reduce((acc, item) => acc + item, 0)
      return colsOffset + (hasCheckbox ? 51 : 0) + (expandableRows ? 25 : 0)
    }
    return index * fixedLeftColsWidth + (hasCheckbox ? 51 : 0) + (expandableRows ? 25 : 0)
  }

  const getActionOffset = (index: number) => {
    return (headData.length - index - 1) * actionWidth
  }

  const getLeftDividerOffset = () => {
    if (customFixedLeftColsWidth.length) {
      const dividerOffset = customFixedLeftColsWidth.reduce((acc, item) => acc + item, 0)
      return dividerOffset + (hasCheckbox ? 51 : 0) + (expandableRows ? 25 : 0)
    }
    return (
      fixedLeftCols.length * fixedLeftColsWidth +
      (hasCheckbox ? 51 : 0) +
      (expandableRows ? 25 : 0)
    )
  }

  const getFixedLeftColWidth = (index: number) => {
    if (customFixedLeftColsWidth.length) {
      return customFixedLeftColsWidth[index]
    }
    return fixedLeftColsWidth
  }

  const getActionDividerOffset = () => {
    return actionColumns.length * actionWidth
  }

  const getColumnAlign = (index: number) => {
    if (customAlign.length) {
      const align = customAlign.find((item) => item.colIndex === index)
      if (align?.textAlign) return align.textAlign
    }
    return undefined
  }

  const expandableRows = hasExpandableRows(bodyData) || hasAsyncExpand
  useEffect(() => {
    if (!isLoading) setExpandedId('')
  }, [isLoading])

  const hasActions = useMemo(() => !!actionColumns.length, [actionColumns])
  const hasFixedCols = useMemo(() => {
    return !!fixedLeftCols.length || hasActions
  }, [fixedLeftCols, hasActions])

  useEffect(() => {
    sortValue && setSortFilter(sortValue)
  }, [sortValue])

  const handleSort = (column: string, sort: string) => {
    if (sort === 'default') {
      setSortFilter({})
      sortHandle && sortHandle({})

      return
    }
    setSortFilter({
      column,
      isCrescent: sort === 'ascendent'
    })

    sortHandle && sortHandle({ column: column, isCrescent: sort === 'ascendent' })
  }

  const renderHeadItem = (item: HeadItemType, sortFilter: SortFilterType) => {
    if (item.columnName === '' || !item.sortable) return item.label

    return (
      <SortLabel
        controlSort={
          sortFilter.column === item.columnName
            ? sortFilter.isCrescent
              ? 'ascendent'
              : 'descendent'
            : 'default'
        }
        label={item.label}
        sortAction={(sort: string) => handleSort(item.columnName, sort)}
      />
    )
  }

  const renderHeadTable = () => {
    const heads: HeadItemType[] = headData as HeadItemType[]

    return [
      ...heads.map((headItem, index) => (
        <Fragment key={headItem.columnName || index}>
          {headItem?.actionsLabel ? (
            <S.ActionsLabel>{renderHeadItem(headItem, sortFilter)}</S.ActionsLabel>
          ) : (
            <>{renderHeadItem(headItem, sortFilter)}</>
          )}
        </Fragment>
      ))
    ]
  }

  const renderSubrow = (row: BodyData) => {
    if (!row.subRows?.length) return
    if (isLoading && expandedId === row.id && row.expanded) return

    return row.subRows.map((subrow, index) => (
      <S.SubRow key={subrow.id} visible={!!row.expanded}>
        {hasCheckbox && <td />}
        <S.ExpandActionWrapper fixed={!!fixedLeftCols.length}>
          <Collapse in={row.expanded} timeout="auto" unmountOnExit>
            {row.subRows?.length === index + 1 ? (
              <S.ExpandLineEnd rowHeight={rowHeight}>
                <div />
              </S.ExpandLineEnd>
            ) : (
              <S.ExpandLine rowHeight={rowHeight}>
                <div />
              </S.ExpandLine>
            )}
          </Collapse>
        </S.ExpandActionWrapper>
        {subrow.content.map((item, subrowIndex) =>
          item ? (
            <S.SubRowItem
              key={`table-subrow-${subrowIndex}`}
              rowHeight={rowHeight}
              isIcon={isIconCol(subrowIndex)}
              textAlign={getColumnAlign(subrowIndex)}
              fixed={isFixedCol(subrowIndex)}
              fixedLeft={isFixedLeft(subrowIndex)}
              fixedLeftOffset={getFixedLeftOffset(subrowIndex)}
              fixedLeftWidth={getFixedLeftColWidth(subrowIndex)}
            >
              <Collapse in={row.expanded} timeout="auto" unmountOnExit>
                {item}
              </Collapse>
            </S.SubRowItem>
          ) : (
            <td key={`empty-subrow-${subrowIndex}`} />
          )
        )}
      </S.SubRow>
    ))
  }

  const tableContentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (onScroll) {
      const container = tableContentRef.current

      const handleScroll = () => {
        if (container) {
          const { scrollTop, scrollHeight, clientHeight } = container

          if (scrollTop + clientHeight >= scrollHeight) {
            onScroll()
          }
        }
      }

      if (container) {
        container.addEventListener('scroll', handleScroll)
      }

      return () => {
        if (container) {
          container.removeEventListener('scroll', handleScroll)
        }
      }
    }
  }, [])

  const renderTable = () => {
    return (
      <S.Wrapper
        maxHeight={maxHeight}
        notScrollable={notScrollable}
        hasFixedCols={hasFixedCols}
        isPrinting={isPrinting}
        ref={tableContentRef}
      >
        <S.WrapperTable ref={tableRef}>
          <S.Table cellSpacing={0} data-testid="table-component">
            <S.TableHead fixed={fixedHead}>
              <S.HeadRow>
                {hasCheckbox && (
                  <S.CheckboxWrapper
                    data-testid="checkbox-header"
                    fixed={!!fixedLeftCols.length}
                  >
                    <Checkbox
                      size={checkboxSize}
                      checked={verifyAllChecked() && !verifyAllDisabled()}
                      indeterminate={verifySomeChecked()}
                      onChange={() => handleGlobalCheckbox()}
                      disabled={disableGlobalCheckbox || verifyAllDisabled()}
                    />
                  </S.CheckboxWrapper>
                )}
                {expandableRows && (
                  <S.ExpandActionWrapper fixed={!!fixedLeftCols.length} />
                )}
                {renderHeadTable().map((item, index) => {
                  return (
                    <S.HeadItem
                      key={`table-head-item-${index}`}
                      fixed={isFixedCol(index)}
                      dense={dense}
                      isIcon={isIconCol(index)}
                      textAlign={getColumnAlign(index)}
                      fixedLeft={isFixedLeft(index)}
                      fixedLeftOffset={getFixedLeftOffset(index)}
                      fixedLeftWidth={getFixedLeftColWidth(index)}
                      fixedAction={isFixedAction(index)}
                      actionOffset={getActionOffset(index)}
                      actionWidth={actionWidth}
                    >
                      {item}
                    </S.HeadItem>
                  )
                })}
              </S.HeadRow>
            </S.TableHead>

            {bodyData?.length ? (
              bodyData.map((row, indexBodyData) => {
                if (!row) return
                return (
                  <Fragment key={indexBodyData}>
                    <S.RowGroup key={`row-${indexBodyData}`} checked={row.checked}>
                      <S.TableRow expanded={row.expanded}>
                        {hasCheckbox && (
                          <S.RowCheckboxWrapper
                            data-testid="checkbox-row"
                            fixed={!!fixedLeftCols.length}
                          >
                            <Checkbox
                              size={checkboxSize}
                              checked={row.checked}
                              onChange={() => setChecked(!row.checked, row.id)}
                              disabled={row.disableCheckbox}
                            />
                          </S.RowCheckboxWrapper>
                        )}
                        {expandableRows && (
                          <S.ExpandActionWrapper fixed={!!fixedLeftCols.length}>
                            {(row.subRows?.length || hasAsyncExpand) && (
                              <>
                                <S.ExpandButton
                                  disabled={isLoading}
                                  onClick={() => {
                                    toggleExpand(row.id)
                                    setExpandedId(row.id)
                                  }}
                                >
                                  {isLoading && expandedId === row.id ? (
                                    <CircularProgress size="15px" color={iconColor} />
                                  ) : row.expanded ? (
                                    <>
                                      <S.CollapseIcon customcolor={iconColor} />
                                      <S.ExpandLineStart
                                        rowHeight={rowHeight}
                                        expanded={row.expanded}
                                      />
                                    </>
                                  ) : (
                                    <S.ExpandIcon customcolor={iconColor} />
                                  )}
                                </S.ExpandButton>
                              </>
                            )}
                          </S.ExpandActionWrapper>
                        )}
                        {/** Main line content **/}
                        {row.content.map((item, indexRow) => {
                          return (
                            <S.RowItem
                              data-testid="row-item-table"
                              dense={dense}
                              valign={valign}
                              rowHeight={rowHeight}
                              key={`table-row-${indexRow}`}
                              distributeWidth={distributeWidth}
                              hasCheckbox={hasCheckbox}
                              isIcon={isIconCol(indexRow)}
                              textAlign={getColumnAlign(indexRow)}
                              inputAlign={inputAlign}
                              cursorPointer={
                                !row.disabled &&
                                (Boolean(row.linkNavigate) ||
                                  Boolean(row.onClick) ||
                                  Boolean(row.onDoubleClick))
                              }
                              fixed={isFixedCol(indexRow)}
                              onClick={() => {
                                if (
                                  row.onClick &&
                                  !actionColumns.includes(indexRow) &&
                                  !row.disabled
                                )
                                  row.onClick()
                              }}
                              onDoubleClick={() => {
                                if (row.onDoubleClick && !row.disabled)
                                  row.onDoubleClick()
                              }}
                              fixedLeft={isFixedLeft(indexRow)}
                              fixedLeftOffset={getFixedLeftOffset(indexRow)}
                              fixedLeftWidth={getFixedLeftColWidth(indexRow)}
                              fixedAction={isFixedAction(indexRow)}
                              actionOffset={getActionOffset(indexRow)}
                              actionWidth={actionWidth}
                              isExpanded={row.expanded}
                            >
                              {row.linkNavigate &&
                              row.colunsNotNavigate &&
                              !row.colunsNotNavigate.includes(indexRow) ? (
                                <S.TableLink
                                  to={row.linkNavigate}
                                  draggable={false}
                                  onMouseDown={handleLinkDown}
                                  onMouseUp={handleLinkUp}
                                  onClick={handleLinkClick}
                                  onMouseMove={handleLinkMove}
                                >
                                  {item}
                                </S.TableLink>
                              ) : (
                                item
                              )}
                            </S.RowItem>
                          )
                        })}
                      </S.TableRow>

                      {renderSubrow(row)}
                    </S.RowGroup>
                  </Fragment>
                )
              })
            ) : (
              <S.RowGroup>
                <S.TableRow>
                  <S.RowItem
                    colSpan={colSpan}
                    rowHeight={rowHeight}
                    distributeWidth={distributeWidth}
                    dense={dense}
                  >
                    <NotFound title={notFoundTitle} description={notFoundDescription} />
                  </S.RowItem>
                </S.TableRow>
              </S.RowGroup>
            )}

            {summaryRow && (
              <tr>
                {hasCheckbox && <td />}
                {summaryRow.map((item, index) => (
                  <S.RowItem
                    dense={dense}
                    rowHeight={rowHeight}
                    key={`summary-row-${index}`}
                    hasCheckbox={hasCheckbox}
                    distributeWidth={distributeWidth}
                  >
                    {item}
                  </S.RowItem>
                ))}
              </tr>
            )}
          </S.Table>
        </S.WrapperTable>
      </S.Wrapper>
    )
  }

  return (
    <>
      {!hasFixedCols && !hasActions ? (
        renderTable()
      ) : (
        <S.ActionTableWrapper isPrinting={isPrinting}>
          {!!fixedLeftCols.length && !!bodyData?.length && (
            <S.FixedDividerLeft offset={getLeftDividerOffset()} />
          )}
          {renderTable()}
          {!!actionColumns.length && !!bodyData?.length && (
            <S.ActionDivider offset={getActionDividerOffset()} />
          )}
        </S.ActionTableWrapper>
      )}
    </>
  )
}

export default memo(Table)
